// ============================================================
// ELC Shift — Uploads / Delivery module (v104.0)
//
// Delivery schedule management — when stock deliveries
// arrive at each store, used by the rota generator to ensure
// stockroom coverage.
//
// Public surface:
//   • loadDeliveryData
//   • saveDeliveryData
//   • renderDelivery
//   • openDeliveryModal
//   • closeDeliveryModal
//   • saveDeliveryModal
//   • deleteDelivery
//
// Free globals consumed:
//   DB, showToast, escapeHtml, formatDateShort, ALL_STORES
// ============================================================

export function loadDeliveryData() {
  try {
    const saved = localStorage.getItem('elc_delivery_data');
    if (saved) return JSON.parse(saved);
  } catch(e) {}
  // Default seed data
  return [
    {store:'Dubai - Dubai Mall',      day:'Tuesday',   windowStart:'07:00', windowEnd:'09:00', notes:'Main weekly delivery',  frequency:'weekly'},
    {store:'Dubai - Dubai Mall 2',    day:'Monday',    windowStart:'07:00', windowEnd:'09:00', notes:'Weekly delivery',        frequency:'weekly'},
    {store:'Dubai - Mercato Mall',    day:'Wednesday', windowStart:'08:00', windowEnd:'10:00', notes:'Bi-weekly',              frequency:'biweekly'},
  ];
}

// v133.97: the error log, 10 Oct 2026: "QuotaExceededError ... Setting the value of 'elc_delivery_data'
// exceeded the quota" (x2). The browser's storage was full (of old cached rota weeks, see db.js cacheSet),
// and the throw happened BEFORE the database write below, so the delivery change was never saved and the
// modal stayed open. The local copy is a convenience; the database is the record. It is now best effort.
function keepLocal(key, value) {
  try { localStorage.setItem(key, value); return true; }
  catch (e) {
    if (typeof DB !== 'undefined' && DB.freeCacheSpace) DB.freeCacheSpace();
    try { localStorage.setItem(key, value); return true; } catch (e2) { return false; }
  }
}

export function saveDeliveryData(data) {
  keepLocal('elc_delivery_data', JSON.stringify(data));
  // Also update legacy deliveryData array used by AI generator
  deliveryData.length = 0;
  data.forEach(d => deliveryData.push({
    store: d.store, day: d.day,
    window: d.windowStart + '-' + d.windowEnd,
    notes: d.notes || ''
  }));
  // v133.97: the old second copy (elc_delivery_schedule, "so the staff app can read delivery days") had
  // no reader anywhere: the staff app reads the delivery table. Removed, and its stale copy cleared.
  try { localStorage.removeItem('elc_delivery_schedule'); } catch (e) { /* storage unavailable */ }
  // v75-3: map UI shape (camelCase, `day`) → DB shape (snake_case, `day_of_week`).
  // The local UI keeps camelCase to avoid widespread refactor; the DB-bound
  // payload is built explicitly here so the schema contract is in one place.
  // Bug history: pre-v75-3 this sent {windowStart, windowEnd, day} which silently
  // 400'd against the actual `delivery` table columns (window_start, window_end,
  // day_of_week). Errors were hidden by .catch(console.warn).
  const dbRows = data.map(d => ({
    store: d.store,
    day_of_week: d.day,
    window_start: d.windowStart,
    window_end: d.windowEnd,
    notes: d.notes || null,
    // Admin saves don't carry per-row staff attribution — admin actions
    // are attributed to the admin account via the Bearer token, which the
    // /admin/delivery endpoint already records server-side.
  }));
  // Write to DB via admin endpoint (admin: true → Bearer token attached).
  // Admin endpoint accepts the array and upserts on (store, day_of_week).
  DB.delivery.saveAdmin(dbRows).catch(e => {
    console.error('[DB] Delivery save failed:', e.message);
    showToast('⚠️ Delivery saved locally but DB sync failed — may not appear to stores', 'error');
  });
}

export function renderDelivery() {
  const tbody = document.getElementById('deliveryBody');
  if (!tbody) return;

  // Populate store filter once
  const storeSel = document.getElementById('deliveryFilterStore');
  if (storeSel && storeSel.options.length <= 1) {
    const stores = [...new Set(staffData.map(s => s.store))].sort();
    stores.forEach(st => {
      const o = document.createElement('option');
      o.value = st; o.textContent = st; storeSel.appendChild(o);
    });
  }

  const storeFilter = document.getElementById('deliveryFilterStore')?.value || '';
  const dayFilter   = document.getElementById('deliveryFilterDay')?.value   || '';
  const data = loadDeliveryData();

  let rows = data;
  if (storeFilter) rows = rows.filter(d => d.store === storeFilter);
  if (dayFilter)   rows = rows.filter(d => d.day   === dayFilter);

  if (!rows.length) {
    tbody.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:24px;color:var(--sub)">No deliveries found — tap + Add Delivery to create one.</td></tr>';
    return;
  }

  const freqLabels = {weekly:'Every week', biweekly:'Every 2 weeks', monthly:'Monthly', adhoc:'Ad hoc'};
  const dayColors  = {Monday:'badge-blue',Tuesday:'badge-green',Wednesday:'badge-blue',Thursday:'badge-green',
                      Friday:'badge-amber',Saturday:'badge-amber',Sunday:'badge-amber'};

  tbody.innerHTML = rows.map((d, i) => {
    // Find actual index in full data for edit/delete
    const realIdx = data.indexOf(d);
    const shortStore = d.store.split(' - ').pop();
    const window = (d.windowStart || '') + (d.windowEnd ? ' – ' + d.windowEnd : d.window || '');
    return `<tr>
      <td translate="no"><strong>${escapeHtml(shortStore||'')}</strong><div style="font-size:11px;color:var(--sub)">${escapeHtml(d.store||'')}</div></td>
      <td><span class="badge ${dayColors[d.day]||'badge-blue'}">${d.day}</span></td>
      <td style="font-weight:600">${window}</td>
      <td style="font-size:12px;color:var(--sub)" translate="no">${escapeHtml(d.notes||'—')}</td>
      <td style="font-size:11px;color:var(--sub)">${freqLabels[d.frequency]||'Weekly'}</td>
      <td style="white-space:nowrap">
        <button class="btn btn-outline btn-sm" onclick="Uploads.delivery.openDeliveryModal(${realIdx})">Edit</button>
        <button class="btn btn-outline btn-sm" style="color:var(--red);border-color:var(--red);margin-inline-start:4px" onclick="Uploads.delivery.deleteDelivery(${realIdx})">Delete</button>
      </td>
    </tr>`;
  }).join('');
}

export function openDeliveryModal(editIdx) {
  const isEdit = editIdx !== undefined && editIdx !== null && editIdx !== '';
  document.getElementById('deliveryModalTitle').textContent = isEdit ? 'Edit Delivery' : 'Add Delivery';
  document.getElementById('dmEditIdx').value = isEdit ? String(editIdx) : '';

  // Populate store dropdown
  const dmStore = document.getElementById('dmStore');
  dmStore.innerHTML = '';
  const stores = [...new Set(staffData.map(s => s.store))].sort();
  stores.forEach(st => {
    const o = document.createElement('option');
    o.value = st; o.textContent = st; dmStore.appendChild(o);
  });

  if (isEdit) {
    const data = loadDeliveryData();
    const d = data[editIdx];
    if (d) {
      dmStore.value = d.store;
      document.getElementById('dmDay').value         = d.day;
      document.getElementById('dmWindowStart').value = d.windowStart || '07:00';
      document.getElementById('dmWindowEnd').value   = d.windowEnd   || '09:00';
      document.getElementById('dmNotes').value       = d.notes || '';
      document.getElementById('dmFrequency').value   = d.frequency || 'weekly';
    }
  } else {
    document.getElementById('dmDay').value         = 'Monday';
    document.getElementById('dmWindowStart').value = '07:00';
    document.getElementById('dmWindowEnd').value   = '09:00';
    document.getElementById('dmNotes').value       = '';
    document.getElementById('dmFrequency').value   = 'weekly';
  }

  document.getElementById('deliveryModal').classList.add('open');
}

export function closeDeliveryModal() {
  document.getElementById('deliveryModal').classList.remove('open');
}

export function saveDeliveryModal() {
  const store       = document.getElementById('dmStore').value;
  const day         = document.getElementById('dmDay').value;
  const windowStart = document.getElementById('dmWindowStart').value;
  const windowEnd   = document.getElementById('dmWindowEnd').value;
  const notes       = document.getElementById('dmNotes').value.trim();
  const frequency   = document.getElementById('dmFrequency').value;
  const editIdxRaw  = document.getElementById('dmEditIdx').value;

  if (!store || !day) { showToast('Select a store and day', 'error'); return; }

  const data = loadDeliveryData();
  const record = { store, day, windowStart, windowEnd, notes, frequency };

  if (editIdxRaw !== '') {
    data[parseInt(editIdxRaw)] = record;
    showToast('Delivery updated', 'success');
  } else {
    data.push(record);
    showToast('Delivery added', 'success');
  }

  saveDeliveryData(data);
  closeDeliveryModal();
  renderDelivery();
}

export async function deleteDelivery(idx) {
  const data = loadDeliveryData();
  if (!(await (window.uiConfirm || function (m) { return Promise.resolve(confirm(m)); })('Delete delivery for ' + (data[idx]?.store || 'this store') + ' on ' + (data[idx]?.day || '') + '?', { danger: true, confirmLabel: 'Delete' }))) return;
  data.splice(idx, 1);
  saveDeliveryData(data);
  showToast('Delivery removed', 'success');
  renderDelivery();
}

